import React, { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { getAllUsers, updateUserRole, deleteUser, resetUserPassword, adjustUserPoints } from '../api';
import { toast } from 'sonner';
import { Search, Trash2, Key, Star, Shield, User, UserCheck } from 'lucide-react';

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Modals
  const [pwdModal, setPwdModal] = useState(null); // userId
  const [newPwd, setNewPwd] = useState('');
  const [ptsModal, setPtsModal] = useState(null); // {id, username}
  const [ptsDelta, setPtsDelta] = useState(0);
  const [ptsReason, setPtsReason] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await getAllUsers(params);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch {
      toast.error('Erreur chargement utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRole = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Rôle mis à jour');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Supprimer "${username}" ? Irréversible.`)) return;
    try {
      await deleteUser(userId);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPassword = async () => {
    if (!newPwd || newPwd.length < 6) { toast.error('Minimum 6 caractères'); return; }
    try {
      await resetUserPassword(pwdModal, newPwd);
      toast.success('Mot de passe réinitialisé');
      setPwdModal(null);
      setNewPwd('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleAdjustPoints = async () => {
    if (!ptsReason) { toast.error('Raison requise'); return; }
    try {
      await adjustUserPoints(ptsModal.id, ptsDelta, ptsReason);
      toast.success('Points ajustés');
      setPtsModal(null);
      setPtsDelta(0);
      setPtsReason('');
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const roleBadge = (role) => {
    if (role === 'superadmin') return <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1"><Shield className="w-3 h-3" />Superadmin</span>;
    if (role === 'creator') return <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1"><UserCheck className="w-3 h-3" />Créateur</span>;
    return <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full flex items-center gap-1"><User className="w-3 h-3" />Viewer</span>;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Utilisateurs</h1>
          <p className="text-muted-foreground text-sm">{total} utilisateurs au total</p>
        </div>

        {/* Filtres */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              className="w-full pl-9 pr-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Rechercher nom ou email..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            value={roleFilter}
            onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
          >
            <option value="">Tous les rôles</option>
            <option value="viewer">Viewer</option>
            <option value="creator">Créateur</option>
            <option value="superadmin">Superadmin</option>
          </select>
          <button onClick={fetchUsers} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors">
            Rechercher
          </button>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground">Chargement...</div>
          ) : users.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">Aucun utilisateur trouvé</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Utilisateur</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Rôle</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Points</th>
                    <th className="px-4 py-3 text-left text-muted-foreground font-medium">Inscription</th>
                    <th className="px-4 py-3 text-right text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{u.username}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          className="text-xs bg-transparent border border-border rounded px-2 py-1 text-foreground focus:outline-none"
                          value={u.role}
                          onChange={e => handleRole(u.id, e.target.value)}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="creator">Créateur</option>
                          <option value="superadmin">Superadmin</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-foreground font-medium">{u.points ?? 0}</span>
                          <button
                            onClick={() => { setPtsModal({ id: u.id, username: u.username }); setPtsDelta(0); setPtsReason(''); }}
                            className="p-1 rounded hover:bg-primary/20 text-primary transition-colors"
                            title="Ajuster points"
                          >
                            <Star className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setPwdModal(u.id); setNewPwd(''); }}
                            className="p-1.5 rounded hover:bg-blue-500/20 text-blue-400 transition-colors"
                            title="Réinitialiser mot de passe"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(u.id, u.username)}
                            className="p-1.5 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg disabled:opacity-40 hover:bg-accent transition-colors"
            >
              Précédent
            </button>
            <span className="text-sm text-muted-foreground">Page {page}/{totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg disabled:opacity-40 hover:bg-accent transition-colors"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Modal reset password */}
      {pwdModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-bold text-foreground">Réinitialiser le mot de passe</h2>
            <input
              type="password"
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Nouveau mot de passe (min. 6 caractères)"
              value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
            />
            <div className="flex gap-3">
              <button onClick={() => setPwdModal(null)} className="flex-1 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 transition-colors">Annuler</button>
              <button onClick={handleResetPassword} className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors">Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajuster points */}
      {ptsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-bold text-foreground">Ajuster les points — {ptsModal.username}</h2>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Delta (positif ou négatif)</label>
              <input
                type="number"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                value={ptsDelta}
                onChange={e => setPtsDelta(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Raison</label>
              <input
                type="text"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Ex: bonus, correction, ..."
                value={ptsReason}
                onChange={e => setPtsReason(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setPtsModal(null)} className="flex-1 py-2 bg-muted rounded-lg text-sm hover:bg-muted/80 transition-colors">Annuler</button>
              <button onClick={handleAdjustPoints} className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors">Appliquer</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
